<template>
  <Portal>
    <Modal
      class="modal"
      title=""
      :is-open="isOpen"
      data-testid="forgot-password-modal"
      size="md-2"
      @close="emit('close')"
    >
      <div
        :class="[
          {
            'modal-wrapper': true,
            'modal-wrapper-first-step': step === ForgotPasswordModalSteps.Input
          }
        ]"
      >
        <div v-if="step === ForgotPasswordModalSteps.Input">
          <HelperViewContextual
            icon="BC0020"
            title="Para recuperar a sua senha precisaremos redefini-la"
            :description="`Um link será enviado para o e-mail ${form.email}`"
            context="neutral"
          />
        </div>
        <HelperViewStatus
          v-else-if="step === ForgotPasswordModalSteps.Success"
          title="Enviamos um e-mail para você redefinir a senha de acesso"
          description="Se o e-mail informado estiver cadastrado, você receberá um link para redefinir sua senha."
          status="positive"
        />
      </div>
      <template #buttonContainer>
        <ButtonContainer>
          <template #button-primary>
            <ButtonPrimary
              class="form-button"
              :label="buttonLabel"
              data-testid="confirm-button"
              :disabled="isLoading"
              :loading="isLoading"
              size="regular"
              @click="handleButtonClick"
            />
          </template>
        </ButtonContainer>
      </template>
    </Modal>
    <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
  </Portal>
</template>
<script setup lang="ts">
import {
  ButtonContainer,
  ButtonPrimary,
  HelperViewContextual,
  HelperViewStatus,
  Modal
} from '@warrenbrasil/nebraska-web';
import { Portal } from '@/modules/common/components/Portal';
import { Ref, computed, inject, ref } from 'vue';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';
import { NotificationTypes } from '@/modules/wallet-builder/src/types';
import { notify } from '@/modules/common/helpers/notification';
import { resetPassword } from '@/modules/common/services/http/account';
import {
  ForgotPasswordModalSteps,
  SignInForm
} from '@/modules/authentication/views/SignIn/types';

const step = ref<ForgotPasswordModalSteps>(ForgotPasswordModalSteps.Input);
const recaptchaToken = ref<string>('');
const recaptcha = ref<IRecaptcha | null>(null);
const form = inject('form') as Ref<SignInForm>;
const isLoading = ref<boolean>(false);

const emit = defineEmits(['close', 'submit']);
defineProps({
  isOpen: { type: Boolean, default: false }
});

const buttonLabel = computed(() => {
  return step.value === ForgotPasswordModalSteps.Input
    ? 'Redefinir senha'
    : 'Ir para o Login';
});

function handleButtonClick() {
  if (step.value === ForgotPasswordModalSteps.Input) {
    handleRecaptcha();
  } else {
    emit('close');
    setTimeout(() => {
      step.value = ForgotPasswordModalSteps.Input;
    }, 500);
  }
}

async function submit() {
  isLoading.value = true;
  try {
    await resetPassword({
      email: form.value.email,
      token: recaptchaToken.value
    });
    step.value = ForgotPasswordModalSteps.Success;
  } catch (err) {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao enviar o e-mail. Por favor, tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

async function handleRecaptcha() {
  isLoading.value = true;
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;

  try {
    await recaptchaEl.onChallenge(RecaptchaActions.RESET_PASSWORD);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
    isLoading.value = false;
  }
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  recaptchaToken.value && (await submit());
}
</script>
<style lang="less" scoped>
:deep(.modal-content) {
  padding: 0 @size-spacing-x800 @size-spacing-x1000 @size-spacing-x800;
}

.helper-view {
  max-width: 464px;
}
.modal {
  z-index: 1000;
  .heading {
    width: 464px;
  }

  &-wrapper {
    max-width: 464px;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: @size-spacing-x350;

    &.modal-wrapper-first-step {
      :deep(.initial-avatar-icon) {
        background: @status-info-background !important;
        color: @status-info !important;
      }
    }
  }

  &-input {
    margin-top: @size-spacing-x500;
  }
}
</style>
